import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
	Form,
	Input,
	Button,
	Card,
	Checkbox,
	Empty,
	Select,
	Tooltip,
	InputNumber,
	Divider,
	Typography,
	Tag,
	Space,
	Radio,
	Popconfirm,
	Badge,
} from 'antd';
import { AnimatePresence } from 'motion/react';
import {
	SearchOutlined,
	QuestionCircleOutlined,
	DollarOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	LoadingOutlined,
	PlusOutlined,
	DeleteOutlined,
	SaveOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { CurrencySelectorComponent } from 'src/_shared/components/currency-selector/CurrencySelectorComponent.jsx';
import getSymbolFromCurrency from 'currency-symbol-map';
import { ListInputComponent } from '../ListInputComponent.jsx';
import { Editor } from '@hugerte/hugerte-react';
import { motion } from 'motion/react';

const RichTextInput = ({ value, onChange, ...editorProps }) => {
	return (
		<Editor
			value={value || ''}
			onEditorChange={(newValue, editor) => {
				onChange?.(editor.getContent({ format: 'html' }));
			}}
			{...editorProps}
		/>
	);
};

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const UNLOCK_TYPES = [
	{ value: 'pointsEarned', label: 'Points Earned' },
	{ value: 'recognitionsEarned', label: 'Recognitions Earned' },
	// IDEAS FOR LATER
	//{ value: 'shoutOutsSent', label: 'ShoutOuts Sent' },
	// { value: 'pointsRedeemed', label: '' },
	// { value: 'referralsAccepted', label: '' },
	// { value: 'referralsHired', label: '' },
	// { value: 'referralsSent', label: '' },
	// { value: 'generalReferralsSent', label: '' },
	// { value: 'internalApplications', label: '' },
	// { value: 'leaderboardPlacement', label: '' },
	// { value: 'socialShares', label: '' },
	// { value: 'nominationsMade', label: '' },
	// { value: 'nominationsReceived', label: '' },
	// { value: 'uniqueContacts', label: '' },
	// { value: 'weeklyLoginStreak', label: '' },
];

const SETTINGS = [
	{
		id: 'recognitionCustomTitle',
		label: 'Custom RecHub Menu Label',
		note: 'Change the RecHub menu label',
		type: 'text',
		placeholder: 'RecHub',
		group: 'general',
		keywords: 'title rechub menu label custom name',
	},
	{
		id: 'customSendRecognitionBoxText',
		label: 'Custom Send Recognition Box Text',
		note: 'This text will appear on RecHub above the Select Recipients / Select Recognition buttons.',
		type: 'richtext',
		placeholder: 'Enter text here - If empty no message will appear.',
		group: 'general',
		keywords: 'send recognition text custom label rechub area message',
	},
	{
		id: 'customSendToPeerText',
		label: 'Custom Send to Peer Button Text',
		note: 'Replaces the "Send to Peer" button text on the View Reward pop-up.',
		type: 'text',
		placeholder: 'Send to Peer',
		group: 'general',
		keywords: 'send to peer button text custom label view reward modal',
	},
	{
		id: 'recognitionAnnouncementText',
		label: 'Recognition Announcements Text',
		note: 'Rich text content that will be displayed in the Recognition Announcements box above My Recognitions on RecHub. If empty, the box will not be displayed at all.',
		type: 'richtext',
		group: 'general',
		keywords:
			'recognition announcement announcements box text rich editor content',
	},
	{
		id: 'customModal.isEnabled',
		label: 'Enable Custom Recognition Pop-up',
		note: 'Enable a custom modal that appears when users send recognition.',
		type: 'checkbox',
		group: 'general',
		keywords: 'custom modal popup recognition enable',
	},
	{
		id: 'customModal.title',
		label: 'Custom Modal Title',
		note: 'Title text for the custom recognition modal.',
		type: 'text',
		placeholder: 'Enter modal title',
		group: 'general',
		keywords: 'custom modal title text',
		dependsOn: 'customModal.isEnabled',
	},
	{
		id: 'customModal.content',
		label: 'Custom Modal Content',
		note: 'Rich text content for the custom recognition modal.',
		type: 'richtext',
		group: 'general',
		keywords: 'custom modal content rich text editor',
		dependsOn: 'customModal.isEnabled',
	},
	{
		id: 'customModal.showEachLogin',
		label: 'Show Modal on Each Login',
		note: 'If enabled, the custom modal will appear every time a user logs in.',
		type: 'checkbox',
		group: 'general',
		keywords: 'custom modal show login each time',
		dependsOn: 'customModal.isEnabled',
	},
	{
		id: 'allowRecognitionsForAccountClaims',
		label: 'Allow Sending Recognitions to Account Claims',
		note: 'If enabled, users can send recognitions to account claims instead of just signed up users.',
		type: 'checkbox',
		group: 'general',
		keywords: 'allow recognitions account claims send users signup',
	},
	{
		id: 'requireBadgeSelection',
		label: 'Require Selection for Recognition Shout-Outs',
		note: 'If enabled, users must select a recognition to be included with a shout-out.',
		type: 'checkbox',
		group: 'general',
		keywords: 'badge selection require mandatory recognitions',
	},
	{
		id: 'requireBadgeAssignmentMessage',
		label: 'Require Message When Sending Recognition',
		note: 'If enabled, users must include a message when sending a shout-out.',
		type: 'checkbox',
		group: 'general',
		keywords:
			'shout out recognition send message require mandatory recognitions text tell us why',
	},
	{
		id: 'disableRecHub',
		label: 'Disable RecHub',
		note: 'Disables the RecHub dashboard social messaging feed, on-the-spot recognition sharing between employees, and switches "My Recognitions" to the default home page. Employees can still submit colleagues for nomination recognitions and earn on-the-spot recognitions from managers.',
		type: 'checkbox',
		group: 'general',
		keywords: 'disable rechub feed dashboard turn off deactivate',
	},
	{
		id: 'disableReplies',
		label: 'Disable Replies on Feed',
		note: 'Disables the ability to reply to Recognitions and shout outs on the social messaging feed.',
		type: 'checkbox',
		group: 'general',
		keywords:
			'disable replies reply comment comments rechub feed dashboard turn off deactivate',
	},
	{
		id: 'disableReplyNotifications',
		label: 'Disable Email Notifications for Replies',
		note: 'Disables the sending of notification emails when replies are made on a Recognition the user received or sent.',
		type: 'checkbox',
		group: 'general',
		keywords:
			'disable replies reply comment comments rechub feed dashboard turn off deactivate notification email emails',
	},
	{
		id: 'disableNominations',
		label: 'Disable Nominations',
		note: 'Disables the Nominations feature and hides the nominations count from "My Recognitions & Rewards" page and the "Nominations" page from the admin sidebar.',
		type: 'checkbox',
		group: 'general',
		keywords:
			'disable nominations nomination nominate recognitions turn off deactivate',
	},
	{
		id: 'allowSelfNominations',
		label: 'Allow Self Nominations',
		note: 'Allow Users to Nominate themselves for Nominations',
		type: 'checkbox',
		group: 'general',
		keywords: 'allow self nominations nominate yourself enable permit',
	},
	{
		id: 'recognitionCategories',
		label: 'Recognition Categories',
		note: "Add your own categories that can be assigned to Recognitions. Enabling you to group and sort Recognitions in the way that works best for your Company's needs.",
		type: 'list',
		group: 'general',
		keywords:
			'categories category group list name recognition custom sort organize',
	},
	{
		id: 'recognitionHashtags',
		label: 'Recognition Hashtags',
		note: 'Add hashtags that users can include in shout-outs and filter the RecHub feed by.',
		type: 'list',
		group: 'general',
		keywords:
			'hashtag hashtags tag tags list name feed filter recognition custom sort organize',
	},
	{
		id: 'catalogOnlyGroupedByCategory',
		label: 'Only Group by Category on Recognition Catalog',
		note: 'Recognition Catalog will display Recognitions by category and hides the toggle for sorting by type.',
		type: 'checkbox',
		group: 'general',
		keywords:
			'categories category group hide recognition sort catalog organize',
	},
	{
		id: 'hideRecognitionSocialShare',
		label: 'Hide social sharing options on badge overview popups',
		type: 'checkbox',
		group: 'display',
		keywords: 'hide social sharing badge popup overview options',
	},
	{
		id: 'hideRecognitionLeaderboard',
		label: 'Hide Leaderboard',
		note: 'Hide the leaderboard on the RecHub page.',
		type: 'checkbox',
		group: 'display',
		keywords: 'hide leaderboard rechub page rankings',
	},
	{
		id: 'hideAllRecognitionsManager',
		label: 'Hide All Recognitions history from Managers',
		note: "Hide the All Recognitions tab on the Manager's Send Recognition page.",
		type: 'checkbox',
		group: 'display',
		keywords:
			'hide disable all recognitions show tab unhide manager page send recognition',
	},
	{
		id: 'recognitionPointsCurrency',
		label: 'Currency for Recognition Cost',
		type: 'currency',
		group: 'financial',
		keywords: 'currency recognition points money dollar format',
	},
	{
		id: 'enableManagerSendForm',
		label: 'Enable Manager Send Form',
		note: 'If enabled, managers will have access to a specialized recognition form to give out recognitions to employees.',
		type: 'checkbox',
		group: 'manager',
		keywords: 'manager send form enable special recognition',
	},
	{
		id: 'requireJustification',
		label: 'Require Justification Form for Recognitions',
		note: 'If enabled, managers and admins must provide a justification before a recognition can be sent.',
		type: 'checkbox',
		group: 'manager',
		keywords: 'justification managerrequire recognize form reason recognition',
	},
	{
		id: 'requireAdminApproval',
		label: 'Require Admin Approval',
		note: 'If enabled, all recognitions will require admin approval before being finalized.',
		type: 'checkbox',
		group: 'manager',
		keywords: 'manager approval require recognize',
	},
	{
		id: 'notificationOnApprovalDecision',
		label: 'Managers Receive Notification when Recognition is Finalized',
		note: 'If enabled, Managers will get a web notification on the completion of an approval decision by an Admin.',
		type: 'checkbox',
		group: 'manager',
		keywords: 'manager approval denial bell notification admin recognition',
	},
	{
		id: 'baseSnappyBudget',
		label: 'Budget Amount Per Employee',
		note: 'This amount determines the base amount each employee is budgeted for receiving Snappy Rewards attached to Recognitions.',
		type: 'number',
		min: 0,
		step: 10,
		group: 'financial',
		keywords: 'budget employee allocation amount money funds per person',
	},
	{
		id: 'nominatedRecognitionsCostPoints',
		label: 'Deduct Reward Cost from Employee Budget for Nominated Recognitions',
		note: "By default, Nominated Recognitions don't count against the employee's budgeted balance. If enabled, the reward cost of the Nominated Recognition will be subtracted from the employee's balance.",
		type: 'checkbox',
		group: 'financial',
		keywords: 'nominated recognitions nomination cost points money funds',
	},
	{
		id: 'allowAdminBalanceOverrides',
		label: 'Allow Admins to Override Balances',
		note: "If checked, admins can add additional funds to an individual employee's balance or send Snappy Grant assigned Recognitions to employees, without a sufficient balance.",
		type: 'checkbox',
		group: 'financial',
		keywords: 'admin override balance override points funds',
	},
];

const SETTING_GROUPS = [
	{ id: 'general', title: 'General Settings' },
	{ id: 'display', title: 'Display Options' },
	{ id: 'manager', title: 'Management Settings' },
	{ id: 'financial', title: 'Financial Settings' },
];

const fuzzySearch = (text, query) => {
	if (!query) return true;
	const pattern = query
		.split('')
		.map((char) => char.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'))
		.join('.*?');
	const regex = new RegExp(pattern, 'i');
	return regex.test(text);
};

const settingStyles = {
	settingRow: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: '16px',
		padding: '12px',
		borderRadius: '4px',
		background: '#fafafa',
		border: '1px solid #f0f0f0',
	},
	settingRowNested: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: '16px',
		marginLeft: '24px',
		padding: '12px',
		borderRadius: '4px',
		background: '#fafafa',
		border: '1px solid #f0f0f0',
		position: 'relative',
	},
	nestedIndicator: {
		position: 'absolute',
		left: '-24px',
		top: '0',
		bottom: '0',
		width: '2px',
		backgroundColor: '#d9d9d9',
		content: '""',
	},
	labelContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		marginBottom: '8px',
	},
	label: {
		fontWeight: '500',
		marginRight: '8px',
		flex: '0 0 auto',
		minWidth: '180px',
		maxWidth: '250px',
	},
	tooltipIcon: { color: '#8c8c8c', fontSize: '16px', marginLeft: '4px' },
	inputContainer: { marginBottom: '4px' },
	noteText: {
		color: '#595959',
		fontSize: '13px',
		marginTop: '4px',
		marginBottom: '0',
	},
	radioLayout: { display: 'flex', flexDirection: 'column', gap: '12px' },
};

const ApiKeyStatusComponent = ({ apiKeyStatus, currentApiKey }) => {
	const getStatusConfig = () => {
		switch (apiKeyStatus) {
			case 'checking':
				return {
					icon: <LoadingOutlined style={{ color: '#1890ff' }} />,
					text: 'Checking status...',
					color: '#1890ff',
				};
			case 'active':
				return {
					icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
					text: 'Active',
					color: '#52c41a',
				};
			case 'inactive':
				return {
					icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
					text: 'Inactive',
					color: '#ff4d4f',
				};
			default:
				return {
					icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
					text: 'None',
					color: '#ff4d4f',
				};
		}
	};
	const statusConfig = getStatusConfig();
	return (
		<div
			style={{
				marginBottom: '24px',
				padding: '16px',
				border: '1px solid #f0f0f0',
				borderRadius: '8px',
				backgroundColor: '#fafafa',
			}}
		>
			<div style={{ marginBottom: '12px' }}>
				<Text strong style={{ fontSize: '16px' }}>
					API Key Status
				</Text>
			</div>
			<div style={{ marginBottom: '8px' }}>
				<Text strong>Current API Key: </Text>
				{currentApiKey?.maskedKey ? (
					<Text code>{currentApiKey.maskedKey}</Text>
				) : (
					<Text type="secondary">None</Text>
				)}
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<Text strong>Status: </Text>
				{statusConfig.icon}
				<Text style={{ color: statusConfig.color, fontWeight: '500' }}>
					{statusConfig.text}
				</Text>
			</div>
		</div>
	);
};

const GrantRow = ({
	snappyGrant,
	snappyCampaigns = [],
	currencySymbol,
	onUpdate,
	onDelete,
	onSave,
	onCancel,
	isDraft = false,
}) => {
	const [isEditingName, setIsEditingName] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [isEditingCampaign, setIsEditingCampaign] = useState(false);

	const [tempName, setTempName] = useState(snappyGrant.name || '');
	const [tempDescription, setTempDescription] = useState(
		snappyGrant.description || ''
	);
	const [tempCampaignId, setTempCampaignId] = useState(null);
	const [saving, setSaving] = useState(false);

	const currentCampaignId = tempCampaignId ?? snappyGrant.snappyCampaignId;
	const currentCampaign = snappyCampaigns.find(
		(c) => c.id === currentCampaignId
	);
	const campaignBudget = currentCampaign?.properties?.[0]?.budget || 0;

	const dirty =
		tempName.trim() !== (snappyGrant.name || '') ||
		tempDescription.trim() !== (snappyGrant.description || '') ||
		(tempCampaignId !== null &&
			tempCampaignId !== snappyGrant.snappyCampaignId);

	const handleSave = async () => {
		if (!dirty || saving) return;

		try {
			setSaving(true);

			if (isDraft && onSave) {
				const currentDraftData = {
					name: tempName.trim(),
					description: tempDescription.trim(),
					snappyCampaignId: currentCampaignId,
				};
				await onSave(currentDraftData);
			} else {
				const patch = {};
				const name = tempName.trim();
				const desc = tempDescription.trim();
				if (name !== (snappyGrant.name || '')) patch.name = name;
				if (desc !== (snappyGrant.description || '')) patch.description = desc;
				if (
					tempCampaignId !== null &&
					tempCampaignId !== snappyGrant.snappyCampaignId
				)
					patch.snappyCampaignId = tempCampaignId;

				await onUpdate?.(snappyGrant.id, patch);
				setIsEditingName(false);
				setIsEditingDescription(false);
				setIsEditingCampaign(false);
				setTempCampaignId(null);
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -10 }}
			transition={{ type: 'spring', stiffness: 500, damping: 35, mass: 0.6 }}
			style={{ marginBottom: 12 }}
		>
			<Card
				size="small"
				style={{
					border: isDraft ? '2px dashed #1890ff' : '1px solid #f0f0f0',
					borderRadius: 8,
					transition: 'all 0.2s ease',
					backgroundColor: isDraft ? '#f6ffed' : '#ffffff',
				}}
				bodyStyle={{ padding: '16px 20px' }}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
					<div
						style={{
							flex: '0 0 auto',
							width: 76,
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
						}}
					>
						<Text
							style={{
								color: 'oklch(0.5307 0.1791 144.1)',
								fontSize: 14,
								fontWeight: 'bold',
							}}
						>
							{currencySymbol}
							{campaignBudget}
						</Text>
					</div>

					<div style={{ flex: '0 0 200px', minWidth: 200 }}>
						{isEditingName ? (
							<Input
								value={tempName}
								onChange={(e) => setTempName(e.target.value)}
								onPressEnter={() => setIsEditingName(false)}
								onBlur={() => setIsEditingName(false)}
								placeholder="Grant name"
								style={{
									fontSize: 14,
									fontWeight: 600,
									border: 'none',
									boxShadow: '0 0 0 2px #1890ff',
									borderRadius: 4,
								}}
								autoFocus
							/>
						) : (
							<div
								onClick={() => setIsEditingName(true)}
								style={{
									fontSize: 14,
									fontWeight: 600,
									cursor: 'text',
									padding: '4px 8px',
									borderRadius: 4,
									minHeight: 32,
									display: 'flex',
									alignItems: 'center',
									border: '1px solid transparent',
									transition: 'all 0.2s ease',
								}}
								onMouseEnter={(e) =>
									(e.target.style.backgroundColor = '#f5f5f5')
								}
								onMouseLeave={(e) =>
									(e.target.style.backgroundColor = 'transparent')
								}
							>
								{tempName || 'Click to add name...'}
							</div>
						)}
					</div>

					<div style={{ flex: 1, minWidth: 200 }}>
						{isEditingDescription ? (
							<TextArea
								value={tempDescription}
								onChange={(e) => setTempDescription(e.target.value)}
								onBlur={() => setIsEditingDescription(false)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault();
										setIsEditingDescription(false);
									}
								}}
								placeholder="Add description..."
								style={{
									fontSize: 13,
									resize: 'none',
									border: 'none',
									boxShadow: '0 0 0 2px #1890ff',
									borderRadius: 4,
									minHeight: 32,
									maxHeight: 80,
								}}
								autoSize={{ minRows: 1, maxRows: 3 }}
								autoFocus
							/>
						) : (
							<div
								onClick={() => setIsEditingDescription(true)}
								style={{
									fontSize: 13,
									cursor: 'text',
									padding: '4px 8px',
									borderRadius: 4,
									minHeight: 32,
									display: 'flex',
									alignItems: 'center',
									border: '1px solid transparent',
									transition: 'all 0.2s ease',
									color: tempDescription ? '#000000d9' : '#00000073',
								}}
								onMouseEnter={(e) =>
									(e.target.style.backgroundColor = '#f5f5f5')
								}
								onMouseLeave={(e) =>
									(e.target.style.backgroundColor = 'transparent')
								}
							>
								{tempDescription || 'Click to add description...'}
							</div>
						)}
					</div>

					<div style={{ flex: '0 0 180px', minWidth: 180 }}>
						{isEditingCampaign ? (
							<Select
								size="small"
								style={{ width: '100%' }}
								showSearch
								optionFilterProp="children"
								defaultOpen
								value={currentCampaignId}
								onChange={(v) => {
									setTempCampaignId(v);
									setIsEditingCampaign(false);
								}}
								onBlur={() => setIsEditingCampaign(false)}
								placeholder="Select campaign"
							>
								{snappyCampaigns.map((c) => (
									<Option key={c.id} value={c.id}>
										{c.name} ({c.id.slice(0, 8)})
									</Option>
								))}
							</Select>
						) : (
							<div
								onClick={() => setIsEditingCampaign(true)}
								style={{
									fontSize: 12,
									cursor: 'pointer',
									padding: '4px 8px',
									borderRadius: 4,
									minHeight: 32,
									display: 'flex',
									alignItems: 'center',
									border: '1px solid transparent',
									transition: 'all 0.2s ease',
									color: '#00000073',
									textDecoration: 'underline dotted',
								}}
								onMouseEnter={(e) =>
									(e.target.style.backgroundColor = '#f5f5f5')
								}
								onMouseLeave={(e) =>
									(e.target.style.backgroundColor = 'transparent')
								}
							>
								Campaign: {currentCampaignId?.slice(0, 8) || 'None'}
							</div>
						)}
					</div>

					<div
						style={{
							flex: '0 0 auto',
							width: 88,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<div
							style={{ width: 32, display: 'flex', justifyContent: 'center' }}
						>
							<Button
								type="text"
								size="small"
								icon={saving ? <LoadingOutlined /> : <SaveOutlined />}
								onClick={handleSave}
								disabled={saving || !dirty}
								style={{
									color: '#1890ff',
									opacity: dirty ? (saving ? 0.6 : 1) : 0,
									visibility: dirty ? 'visible' : 'hidden',
								}}
							/>
						</div>

						<div
							style={{ width: 32, display: 'flex', justifyContent: 'center' }}
						>
							<Button
								type="text"
								size="small"
								icon={<DeleteOutlined />}
								danger
								onClick={() => onDelete?.(snappyGrant.id)}
								style={{ opacity: 0.6 }}
							/>
						</div>
					</div>
				</div>
			</Card>
		</motion.div>
	);
};

const GrantsList = ({
	snappyGrants = [],
	snappyCampaigns = [],
	currencySymbol,
	onInlineEditSave,
	onDelete,
	onCreateGrant,
}) => {
	const [draftGrant, setDraftGrant] = useState(null);

	const handleUpdate = async (id, patch) => {
		if (id === 'draft') {
			setDraftGrant((prev) => ({ ...prev, ...patch }));
			return;
		}

		const grant = snappyGrants.find((g) => g.id === id);
		await onInlineEditSave?.(id, { ...grant, ...patch });
	};

	const handleCreateNew = () => {
		if (snappyCampaigns.length === 0) return;

		const newDraftGrant = {
			id: 'draft',
			name: '',
			description: '',
			snappyCampaignId: snappyCampaigns[0].id,
			isDraft: true,
		};

		setDraftGrant(newDraftGrant);
	};

	const handleSaveDraft = async (draftData) => {
		if (!draftData || !draftData.name?.trim()) {
			return;
		}
		try {
			await onCreateGrant?.(draftData);
			setDraftGrant(null);
		} catch (error) {
			console.error('Error saving draft:', error);
		}
	};

	const handleCancelDraft = () => {
		setDraftGrant(null);
	};

	const handleDelete = async (grantId) => {
		await onDelete?.(grantId);
	};

	const AddNewRow = () => (
		<motion.div
			layout
			whileHover={{ scale: 1.01 }}
			whileTap={{ scale: 0.99 }}
			transition={{ type: 'spring', stiffness: 500, damping: 35, mass: 0.6 }}
			style={{ marginBottom: 12 }}
		>
			<Card
				size="small"
				hoverable
				onClick={handleCreateNew}
				style={{
					border: '2px dashed #d9d9d9',
					borderRadius: 8,
					cursor: 'pointer',
					transition: 'all 0.2s ease',
					backgroundColor: '#fafafa',
				}}
				bodyStyle={{
					padding: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						color: '#595959',
					}}
				>
					<PlusOutlined style={{ fontSize: 20 }} />
					<Text type="secondary" style={{ fontSize: 14, fontWeight: 500 }}>
						Add New Snappy Reward
					</Text>
				</div>
			</Card>
		</motion.div>
	);

	return (
		<div style={{ maxWidth: '100%' }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 16,
					padding: '12px 20px',
					backgroundColor: '#fafafa',
					borderRadius: 8,
					marginBottom: 12,
					fontSize: 12,
					fontWeight: 600,
					color: '#666',
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
				}}
			>
				<div style={{ flex: '0 0 auto', width: 76 }}>Budget</div>
				<div style={{ flex: '0 0 200px', minWidth: 200 }}>Name</div>
				<div style={{ flex: 1, minWidth: 200 }}>Description</div>
				<div style={{ flex: '0 0 180px', minWidth: 180 }}>Campaign</div>
				<div style={{ flex: '0 0 auto', width: 88, textAlign: 'center' }}>
					Actions
				</div>
			</div>

			<div>
				{snappyGrants.map((grant) => (
					<GrantRow
						key={grant.id}
						snappyGrant={grant}
						snappyCampaigns={snappyCampaigns}
						currencySymbol={currencySymbol}
						onUpdate={handleUpdate}
						onDelete={() => handleDelete(grant.id)}
					/>
				))}

				{draftGrant && (
					<GrantRow
						key="draft"
						snappyGrant={draftGrant}
						snappyCampaigns={snappyCampaigns}
						currencySymbol={currencySymbol}
						onUpdate={handleUpdate}
						onSave={handleSaveDraft}
						onCancel={handleCancelDraft}
						isDraft={true}
					/>
				)}
			</div>

			{!draftGrant && snappyCampaigns.length > 0 && <AddNewRow />}

			{snappyGrants.length === 0 && !draftGrant && (
				<div style={{ textAlign: 'center', padding: '40px 20px' }}>
					<Empty
						description="No Snappy Rewards yet"
						style={{ marginBottom: 16 }}
					/>
					{snappyCampaigns.length > 0 && (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={handleCreateNew}
						>
							Create Your First Snappy Reward
						</Button>
					)}
				</div>
			)}
		</div>
	);
};

const TieredRewardsBuilder = ({ form, snappyGrants = [], currencySymbol }) => {
	const grantsById = useMemo(() => {
		const map = new Map();
		snappyGrants.forEach((g) => map.set(g.id, g));
		return map;
	}, [snappyGrants]);

	const addTier = (add) => {
		const firstGrantId = snappyGrants[0]?.id;
		add({
			id: uuidv4(),
			snappyGrantId: firstGrantId,
			name: 'New Tier',
			description: '',
			unlockConditionType: UNLOCK_TYPES[0].value,
			unlockConditionAmount: 1,
		});
	};

	return (
		<div
			style={{
				background: '#fafafa',
				border: '1px solid #f0f0f0',
				borderRadius: 8,
				padding: 12,
			}}
		>
			<Form.List name="tieredRewardsCampaign">
				{(fields, { add, remove, move }) => {
					return (
						<>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									marginBottom: 10,
								}}
							>
								<Text strong>Tiered Rewards</Text>
								<Button
									icon={<PlusOutlined />}
									type="primary"
									onClick={() => addTier(add)}
								>
									Add Tier
								</Button>
							</div>

							<div
								style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
									gap: 16,
									maxWidth: '100%',
									overflowX: 'auto',
								}}
							>
								{fields.map((field, idx) => (
									<motion.div
										key={field.key}
										layout
										initial={{ opacity: 0, x: 20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: -20 }}
										transition={{ type: 'spring', stiffness: 500, damping: 35 }}
										style={{ minWidth: 300 }}
									>
										<Card
											size="small"
											bodyStyle={{ padding: 12 }}
											style={{
												borderRadius: 10,
												height: '100%',
												border: `2px solid ${idx === 0 ? '#52c41a' : idx === fields.length - 1 ? '#faad14' : '#1890ff'}`,
												position: 'relative',
											}}
											title={
												<div
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
													}}
												>
													<Space align="center">
														<span style={{ fontWeight: 'bold' }}>
															Tier {idx + 1}
														</span>
														<Tag
															color={
																idx === 0
																	? 'green'
																	: idx === fields.length - 1
																		? 'gold'
																		: 'blue'
															}
														>
															{form
																.getFieldValue([
																	'tieredRewardsCampaign',
																	field.name,
																	'id',
																])
																?.slice(0, 8) || 'new'}
														</Tag>
													</Space>
													<Popconfirm
														title="Remove this tier?"
														onConfirm={() => remove(field.name)}
													>
														<Button
															icon={<DeleteOutlined />}
															size="small"
															danger
														/>
													</Popconfirm>
												</div>
											}
										>
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: 12,
												}}
											>
												<Form.Item
													name={[field.name, 'name']}
													label="Tier Name"
													rules={[
														{
															required: true,
															message: 'Tier name is required',
														},
													]}
													style={{ marginBottom: 0 }}
												>
													<Input placeholder="Tier name" />
												</Form.Item>

												<Form.Item
													name={[field.name, 'description']}
													label="Description"
													style={{ marginBottom: 0 }}
												>
													<TextArea
														placeholder="Description (optional)"
														rows={2}
													/>
												</Form.Item>

												<Form.Item
													name={[field.name, 'snappyGrantId']}
													label="Linked Reward"
													rules={[
														{ required: true, message: 'Select a Reward' },
													]}
													style={{ marginBottom: 0 }}
												>
													<Select
														placeholder="Select Reward"
														showSearch
														optionFilterProp="children"
													>
														{snappyGrants.map((g) => (
															<Option key={g.id} value={g.id}>
																{g.name || 'Untitled'} ({g.id.slice(0, 8)})
															</Option>
														))}
													</Select>
												</Form.Item>

												<div
													style={{
														display: 'grid',
														gridTemplateColumns: '1fr 1fr',
														gap: 8,
													}}
												>
													<Form.Item
														name={[field.name, 'unlockConditionType']}
														label="Unlock Condition"
														rules={[
															{ required: true, message: 'Choose a condition' },
														]}
														style={{ marginBottom: 0 }}
													>
														<Select placeholder="Condition">
															{UNLOCK_TYPES.map((t) => (
																<Option key={t.value} value={t.value}>
																	{t.label}
																</Option>
															))}
														</Select>
													</Form.Item>

													<Form.Item
														name={[field.name, 'unlockConditionAmount']}
														label="Amount"
														rules={[
															{ required: true, message: 'Amount required' },
															{
																type: 'number',
																min: 1,
																message: 'Must be ≥ 1',
															},
														]}
														style={{ marginBottom: 0 }}
													>
														<InputNumber min={1} style={{ width: '100%' }} />
													</Form.Item>
												</div>

												<Form.Item
													shouldUpdate={(prevValues, currentValues) => {
														const prevGrantId =
															prevValues?.tieredRewardsCampaign?.[field.name]
																?.snappyGrantId;
														const currentGrantId =
															currentValues?.tieredRewardsCampaign?.[field.name]
																?.snappyGrantId;
														return prevGrantId !== currentGrantId;
													}}
													style={{ marginBottom: 0 }}
												>
													{() => {
														const grantId = form.getFieldValue([
															'tieredRewardsCampaign',
															field.name,
															'snappyGrantId',
														]);
														const g = grantsById.get(grantId);
														const cost = g?.costInCurrency ?? 0;
														return (
															<div
																style={{
																	padding: '8px 12px',
																	backgroundColor: '#f5f5f5',
																	borderRadius: '6px',
																	fontSize: '12px',
																	fontWeight: 'bold',
																	textAlign: 'center',
																}}
															>
																Cost:{' '}
																<span
																	style={{
																		color: 'oklch(0.5307 0.1791 144.1)',
																	}}
																>
																	{currencySymbol}
																	{cost}
																</span>
															</div>
														);
													}}
												</Form.Item>

												<Form.Item name={[field.name, 'id']} hidden>
													<Input />
												</Form.Item>
											</div>
										</Card>
									</motion.div>
								))}
							</div>

							{fields.length === 0 && (
								<Empty
									description="No tiers yet. Click Add Tier to get started."
									style={{ marginTop: 8 }}
								/>
							)}
						</>
					);
				}}
			</Form.List>
		</div>
	);
};

const renderSettingField = (setting, deps) => {
	const tooltip = setting.note ? (
		<Tooltip title={setting.note}>
			<QuestionCircleOutlined style={settingStyles.tooltipIcon} />
		</Tooltip>
	) : null;

	const isDependent = !!setting.dependsOn;
	if (setting.dependsOn) {
		const dependentValue = deps.form.getFieldValue(setting.dependsOn);
		if (!dependentValue) {
			return null;
		}
	}

	const containerStyle = isDependent
		? settingStyles.settingRowNested
		: settingStyles.settingRow;

	switch (setting.type) {
		case 'checkbox':
			const checkboxContent = (
				<div style={containerStyle}>
					{isDependent && <div style={settingStyles.nestedIndicator}></div>}
					<div style={settingStyles.labelContainer}>
						<Form.Item
							name={setting.id}
							valuePropName="checked"
							style={{ marginBottom: 0 }}
						>
							<Checkbox>
								<span style={{ fontWeight: '500' }}>{setting.label}</span>
							</Checkbox>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

			return isDependent ? (
				<AnimatePresence mode="wait">
					<motion.div
						key={setting.id}
						initial={{ opacity: 0, height: 0, marginBottom: 0 }}
						animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
						exit={{
							opacity: 0,
							height: 0,
							marginBottom: 0,
							transition: {
								duration: 0.25,
								ease: 'easeInOut',
								opacity: { duration: 0.15 },
								height: { duration: 0.3, delay: 0.1 },
							},
						}}
						transition={{
							duration: 0.3,
							ease: 'easeInOut',
							height: { duration: 0.4 },
							opacity: { duration: 0.2, delay: 0.1 },
						}}
						style={{ overflow: 'hidden' }}
					>
						{checkboxContent}
					</motion.div>
				</AnimatePresence>
			) : (
				checkboxContent
			);

		case 'tier':
			return (
				<div style={settingStyles.settingRow}>
					<TieredRewardsBuilder
						form={deps.form}
						snappyGrants={deps.snappyGrants}
						currencySymbol={deps.currencySymbol}
					/>
				</div>
			);

		case 'list':
			return (
				<div style={containerStyle}>
					{isDependent && <div style={settingStyles.nestedIndicator}></div>}
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item name={setting.id} style={{ marginBottom: 0 }}>
							<ListInputComponent setting={setting} />
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

		case 'number':
			return (
				<div style={settingStyles.settingRow}>
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item
							name={setting.id}
							rules={[
								{ required: true, message: 'Please input the required value!' },
							]}
							style={{ marginBottom: 0 }}
						>
							<InputNumber
								prefix={<DollarOutlined />}
								min={setting.min || 0}
								step={setting.step || 1}
								style={{ width: '100%', maxWidth: '200px' }}
							/>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

		case 'radio':
			return (
				<div style={settingStyles.settingRow}>
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item
							name={setting.id}
							rules={[{ required: true, message: 'Please select an option!' }]}
							style={{ marginBottom: 0 }}
						>
							<Radio.Group style={settingStyles.radioLayout}>
								{setting.options?.map((option) => (
									<Radio key={option.value} value={option.value}>
										{option.label}
									</Radio>
								))}
							</Radio.Group>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

		case 'currency':
			return (
				<div style={settingStyles.settingRow}>
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item name={setting.id} style={{ marginBottom: 0 }}>
							<CurrencySelectorComponent />
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

		case 'textarea':
			return (
				<div style={settingStyles.settingRow}>
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item
							name={setting.id}
							rules={[{ required: false }]}
							style={{ marginBottom: 0 }}
						>
							<Input.TextArea
								placeholder={setting.placeholder || ''}
								style={{ maxWidth: '350px', borderRadius: '8px' }}
							/>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

		case 'richtext':
			const richtextContent = (
				<div style={containerStyle}>
					{isDependent && <div style={settingStyles.nestedIndicator}></div>}
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item
							name={setting.id}
							rules={[{ required: false }]}
							style={{ marginBottom: 0 }}
						>
							<RichTextInput
								key={setting.id}
								init={{
									height: 350,
									menubar: false,
									branding: false,
									plugins: ['quickbars', 'lists', 'link'],
									toolbar:
										'undo redo fontsizeinput bold italic forecolor backcolor link | \
                            styles alignleft aligncenter alignright alignjustify bullist numlist outdent indent',
									placeholder: setting.placeholder || 'Enter text',
									quickbars_selection_toolbar:
										'bold italic forecolor backcolor removeformat link',
									quickbars_image_toolbar: false,
									quickbars_insert_toolbar: false,
									toolbar_mode: 'floating',
									link_default_target: '_blank',
									link_assume_external_targets: true,
									link_context_toolbar: true,
								}}
							/>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

			return isDependent ? (
				<AnimatePresence mode="wait">
					<motion.div
						key={setting.id}
						initial={{ opacity: 0, height: 0, marginBottom: 0 }}
						animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
						exit={{
							opacity: 0,
							height: 0,
							marginBottom: 0,
							transition: {
								duration: 0.25,
								ease: 'easeInOut',
								opacity: { duration: 0.15 },
								height: { duration: 0.3, delay: 0.1 },
							},
						}}
						transition={{
							duration: 0.3,
							ease: 'easeInOut',
							height: { duration: 0.4 },
							opacity: { duration: 0.2, delay: 0.1 },
						}}
						style={{ overflow: 'hidden' }}
					>
						{richtextContent}
					</motion.div>
				</AnimatePresence>
			) : (
				richtextContent
			);

		case 'text':
		default:
			const textContent = (
				<div style={containerStyle}>
					{isDependent && <div style={settingStyles.nestedIndicator}></div>}
					<div style={settingStyles.labelContainer}>
						<div style={settingStyles.label}>{setting.label}</div>
					</div>
					<div style={settingStyles.inputContainer}>
						<Form.Item
							name={setting.id}
							rules={[{ required: false }]}
							style={{ marginBottom: 0 }}
						>
							<Input
								placeholder={setting.placeholder || ''}
								style={{ maxWidth: '350px', borderRadius: '8px' }}
							/>
						</Form.Item>
					</div>
					{setting.note && <p style={settingStyles.noteText}>{setting.note}</p>}
				</div>
			);

			return isDependent ? (
				<AnimatePresence mode="wait">
					<motion.div
						key={setting.id}
						initial={{ opacity: 0, height: 0, marginBottom: 0 }}
						animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
						exit={{
							opacity: 0,
							height: 0,
							marginBottom: 0,
							transition: {
								duration: 0.25,
								ease: 'easeInOut',
								opacity: { duration: 0.15 },
								height: { duration: 0.3, delay: 0.1 },
							},
						}}
						transition={{
							duration: 0.3,
							ease: 'easeInOut',
							height: { duration: 0.4 },
							opacity: { duration: 0.2, delay: 0.1 },
						}}
						style={{ overflow: 'hidden' }}
					>
						{textContent}
					</motion.div>
				</AnimatePresence>
			) : (
				textContent
			);
	}
};

const RecognitionSettingsPresentation = ({
	currentUser,
	snappyCampaigns,
	snappyGrants,
	onCreateCampaign,
	handleUpdateCompany,
	showCreateCampaignForm,
	setShowCreateCampaignForm,
	onInlineEditSave,
	form,
	createCampaignForm,
	onDelete,
	onCreateGrant,
	apiKeyStatus,
	currentApiKey,
}) => {
	const currencyCode =
		currentUser?.company?.recognitionSettings?.recognitionPointsCurrency ||
		'USD';
	const currencySymbol = getSymbolFromCurrency(currencyCode);

	const [searchQuery, setSearchQuery] = useState('');
	const [tieredRewardsForm] = Form.useForm();
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [initialFormValues, setInitialFormValues] = useState({});

	const stickyHeaderRef = useRef(null);

	const filteredSettings = SETTINGS.filter((setting) => {
		const searchableText = `${setting.label} ${setting.note || ''} ${setting.keywords || ''}`;
		return fuzzySearch(searchableText, searchQuery);
	});

	const visibleGroups = SETTING_GROUPS.filter((group) => {
		if (searchQuery === '') return true;
		return filteredSettings.some((setting) => setting.group === group.id);
	});
	const showCompanySettings = searchQuery === '' || filteredSettings.length > 0;

	const getInitialValues = () => {
		const initialValues = currentUser?.company?.recognitionSettings || {};
		const customModal = initialValues?.customModal || {};

		return {
			...initialValues,
			recognitionPointsCurrency:
				initialValues?.recognitionPointsCurrency || 'USD',
			baseSnappyBudget: initialValues?.baseSnappyBudget ?? 0,
			recognitionAnnouncementText:
				initialValues?.recognitionAnnouncementText || '',
			'customModal.isEnabled': customModal?.isEnabled || false,
			'customModal.title': customModal?.title || '',
			'customModal.content': customModal?.content || '',
			'customModal.showEachLogin': customModal?.showEachLogin || false,
		};
	};

	useEffect(() => {
		const initialValues = getInitialValues();
		setInitialFormValues(initialValues);
	}, [currentUser]);

	const checkForUnsavedChanges = (currentValues) => {
		if (!initialFormValues || Object.keys(initialFormValues).length === 0)
			return;

		const hasChanges = Object.keys(initialFormValues).some((key) => {
			const initialValue = initialFormValues[key];
			const currentValue = currentValues[key];

			if (typeof initialValue === 'object' && initialValue !== null) {
				return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
			}
			return initialValue !== currentValue;
		});

		setHasUnsavedChanges(hasChanges);
	};

	const handleFormSubmit = async (values) => {
		const otherValues = { ...values };
		const customModal = {
			isEnabled: values['customModal.isEnabled'] || false,
			title: values['customModal.title'] || '',
			content: values['customModal.content'] || '',
			showEachLogin: values['customModal.showEachLogin'] || false,
		};
		delete otherValues['customModal.isEnabled'];
		delete otherValues['customModal.title'];
		delete otherValues['customModal.content'];
		delete otherValues['customModal.showEachLogin'];
		delete otherValues.tieredRewardsCampaign;

		const finalValues = {
			...otherValues,
			customModal,
		};

		await handleUpdateCompany(finalValues);
		setHasUnsavedChanges(false);
		setInitialFormValues(getInitialValues());
	};

	return (
		<div>
			<div style={{ position: 'relative' }}>
				<div
					ref={stickyHeaderRef}
					style={{
						position: 'sticky',
						top: 0,
						zIndex: 1000,
						backgroundColor: '#ffffff00',
						borderBottom: '1px solid #f0f0f0',
						border: 'none',
						transition: 'all 0.2s ease-in-out',
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: 0,
							padding: 0,
						}}
					>
						<div
							style={{
								width: 600,
								marginTop: 20,
								display: 'flex',
								padding: '0 20px',
								justifyContent: 'flex-end',
								gap: '16px',
								alignItems: 'center',
							}}
						>
							<div
								style={{
									flex: 1,
									maxWidth: '500px',
									boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
									borderRadius: '8px',
								}}
							>
								<Input
									prefix={<SearchOutlined />}
									placeholder="Search settings..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									style={{ width: '100%', borderRadius: '8px' }}
									allowClear
									size="large"
								/>
							</div>

							{showCompanySettings && (
								<Button
									type="primary"
									size="large"
									onClick={() => form.submit()}
									style={{
										borderRadius: '20px',
										padding: '0 24px',
										height: '42px',
										fontWeight: '500',
										minWidth: '140px',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
									}}
									disabled={!hasUnsavedChanges}
								>
									{hasUnsavedChanges ? 'Save Changes' : 'Save Changes'}
								</Button>
							)}
						</div>
					</div>
					{searchQuery && (
						<div
							style={{
								marginTop: '8px',
								fontSize: '13px',
								color: '#666',
								textAlign: 'end',
								marginRight: 20,
							}}
						>
							<span>Found {filteredSettings.length} matching settings</span>
						</div>
					)}
				</div>

				{showCompanySettings && (
					<>
						<div className="page-title">
							<h2 className="page-heading-manage-reward">
								Recognition Settings
							</h2>
						</div>
						<div className="setting-card">
							<h4 className="setting-card-title">Recognition Customizations</h4>
							<Form
								layout="vertical"
								onFinish={handleFormSubmit}
								initialValues={getInitialValues()}
								form={form}
								onValuesChange={(_, allValues) =>
									checkForUnsavedChanges(allValues)
								}
							>
								{searchQuery ? (
									<div>
										{filteredSettings.map((setting) => (
											<div key={setting.id}>
												{renderSettingField(setting, {
													form,
													snappyGrants,
													currencySymbol,
												})}
											</div>
										))}
									</div>
								) : (
									<>
										{visibleGroups.map((group) => (
											<div key={group.id} style={{ marginBottom: '32px' }}>
												<Divider orientationMargin={10} orientation="left">
													<span
														style={{ fontSize: '16px', fontWeight: 'bold' }}
													>
														{group.title}
													</span>
												</Divider>
												<div
													style={{
														maxWidth: group.id !== 'snappy' ? '900px' : '100%',
													}}
												>
													<Form.Item
														shouldUpdate={(prevValues, currentValues) =>
															prevValues['customModal.isEnabled'] !==
															currentValues['customModal.isEnabled']
														}
													>
														{() => (
															<>
																{SETTINGS.filter(
																	(s) => s.group === group.id
																).map((setting) => (
																	<div key={setting.id}>
																		{renderSettingField(setting, {
																			form,
																			snappyGrants,
																			currencySymbol,
																		})}
																	</div>
																))}
															</>
														)}
													</Form.Item>
												</div>
											</div>
										))}
									</>
								)}
							</Form>
						</div>
					</>
				)}
			</div>

			<>
				<div className="page-title">
					<h2 className="page-heading-manage-reward">Snappy Configuration</h2>
				</div>

				<ApiKeyStatusComponent
					apiKeyStatus={apiKeyStatus}
					currentApiKey={currentApiKey}
				/>

				{apiKeyStatus === 'inactive' && (
					<Card>
						<Empty description="No Snappy integration found, would you like to set one up?" />
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								marginTop: '16px',
							}}
						>
							<Button
								type="primary"
								onClick={() => setShowCreateCampaignForm(true)}
								style={{ width: '220px' }}
							>
								Connect with Snappy API Key
							</Button>
						</div>
					</Card>
				)}

				{showCreateCampaignForm && (
					<Card title="Connect Snappy" style={{ marginTop: '16px' }}>
						<Form
							form={createCampaignForm}
							layout="vertical"
							onFinish={onCreateCampaign}
						>
							<Form.Item
								name="snappyAPIKey"
								label="Snappy API Key"
								rules={[
									{
										required: true,
										message: 'Please input the Snappy API Key!',
									},
								]}
							>
								<Input placeholder="Enter Snappy API Key" />
							</Form.Item>

							<Form.Item>
								<Button type="primary" htmlType="submit">
									Save
								</Button>
								<Button
									style={{ marginLeft: '8px' }}
									onClick={() => setShowCreateCampaignForm(false)}
								>
									Cancel
								</Button>
							</Form.Item>
						</Form>
					</Card>
				)}

				<div style={{ marginTop: 16 }}>
					<Card>
						<div style={{ marginBottom: 16 }}>
							<Text strong style={{ fontSize: 16 }}>
								Snappy Rewards List
							</Text>
							<div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
								Manage your Snappy rewards that can be assigned to recognitions
								and used with reward tiers.
							</div>
						</div>
						<GrantsList
							snappyGrants={snappyGrants}
							snappyCampaigns={snappyCampaigns}
							currencySymbol={currencySymbol}
							onInlineEditSave={onInlineEditSave}
							onDelete={onDelete}
							onCreateGrant={onCreateGrant}
						/>
					</Card>
				</div>

				{snappyGrants.length > 0 && (
					<div style={{ marginTop: 24 }}>
						<Card>
							<div style={{ marginBottom: 16 }}>
								<Text strong style={{ fontSize: 16 }}>
									Tiered Rewards Campaign
								</Text>
								<div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
									Create an engaging tiered rewards campaign using Snappy
									Rewards and a customizable title, message, and unlock
									condition for each tier!
								</div>
							</div>
							<Form
								form={tieredRewardsForm}
								layout="vertical"
								initialValues={{
									tieredRewardsCampaign:
										currentUser?.company?.recognitionSettings
											?.tieredRewardsCampaign || [],
								}}
								onFinish={async (values) => {
									const tieredRewardsCampaign =
										values.tieredRewardsCampaign || [];
									await handleUpdateCompany({ tieredRewardsCampaign });
								}}
							>
								<TieredRewardsBuilder
									form={tieredRewardsForm}
									snappyGrants={snappyGrants}
									currencySymbol={currencySymbol}
								/>
								<div style={{ marginTop: 16, textAlign: 'center' }}>
									<Button
										type="primary"
										htmlType="submit"
										style={{
											borderRadius: '20px',
											padding: '0 24px',
											height: '42px',
											fontWeight: '500',
										}}
									>
										Save Tiered Campaign
									</Button>
								</div>
							</Form>
						</Card>
					</div>
				)}
			</>
		</div>
	);
};

export default RecognitionSettingsPresentation;
